import { Button, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { PlusIcon } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { authClient } from "@/modules/auth/presentation/api/client";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { formatCalendarMonthLabel } from "../lib/event-calendar-ui";
import { useDeleteRequest, useRequestsQuery } from "../api/queries";
import { EventViewToggle } from "../ui/EventViewToggle";
import { PerformanceRequestCalendar } from "../ui/PerformanceRequestCalendar";
import { RequestFilter } from "../ui/RequestFilter";
import { RequestTable } from "../ui/RequestTable";

const DEFAULT_SORT = [{ field: "createdAt", dir: "desc" as const }];

export type RequestsPageSearch = OffsetPageQueryDTO & {
  view?: "list" | "calendar";
  calendarMonth?: string;
};

export function RequestsPage() {
  const nav = useNavigate({ from: "/app/performance/requests" });
  const search = useSearch({
    from: "/app/performance/requests",
  }) as RequestsPageSearch;
  const sort = search.sort?.length ? search.sort : DEFAULT_SORT;
  const view = search.view ?? "list";
  const calendarMonth =
    search.calendarMonth ?? formatCalendarMonthLabel(new Date());
  const { data: session } = authClient.useSession();
  const userId = session?.user?.id;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const currentTab = filters.some(
    (f) => f.field === "assignedTo" && f.op === "eq" && f.value === userId,
  )
    ? "mine"
    : filters.some((f) => f.field === "priorityOrder" && f.op === "isNotNull")
      ? "priority"
      : "all";

  const list = useRequestsQuery({
    offset: search.offset,
    limit: search.limit,
    sort,
    filters: search.filters,
  });

  const canCreate = useActionPermission(["performance-requests:create"]);
  const canEdit = useActionPermission(["performance-requests:update"]);
  const canCreateEvent = useActionPermission(["performance-events:create"]);
  const canConvert = canEdit && canCreateEvent;
  const deleteRequest = useDeleteRequest();

  const updateSearch = (patch: Partial<RequestsPageSearch>) => {
    nav({ search: { ...search, ...patch, offset: patch.offset ?? 0 } });
  };

  function applyTab(tab: "all" | "mine" | "priority") {
    const nextFilters = filters.filter(
      (f) => f.field !== "assignedTo" && f.field !== "priorityOrder",
    );
    if (tab === "mine" && userId) {
      nextFilters.push({ field: "assignedTo", op: "eq", value: userId });
    }
    if (tab === "priority") {
      nextFilters.push({
        field: "priorityOrder",
        op: "isNotNull",
        value: null,
      });
    }
    updateSearch({
      filters: nextFilters.length ? nextFilters : undefined,
    });
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[{ label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" }, { label: "ຄິວ" }]}
        />
      </Header>

      <Main>
        <div className="mb-2 flex flex-wrap items-center justify-between space-y-2 gap-3">
          <div>
            <h2 className="flex items-center font-bold text-2xl tracking-tight">
              ຄິວ
            </h2>
            <p className="text-muted-foreground">
              ຈັດການຄຳຂໍຂອງລູກຄ້າກ່ອນແປງເປັນງານສະແດງ.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <EventViewToggle
              view={view}
              onChange={(nextView) => updateSearch({ view: nextView })}
            />
            {canCreate && (
              <Button
                onClick={() => nav({ to: "/app/performance/requests/create" })}
              >
                <PlusIcon className="h-4 w-4" /> ສ້າງຄຳຂໍໃໝ່
              </Button>
            )}
          </div>
        </div>

        <div className="mb-4 space-y-2">
          <div className="inline-flex rounded-md border bg-muted p-1">
            {[
              { id: "all" as const, label: "ທັງໝົດ" },
              { id: "mine" as const, label: "ຂອງຂ້ອຍ" },
              { id: "priority" as const, label: "ສຳຄັນກ່ອນ" },
            ].map((tab) => (
              <Button
                key={tab.id}
                type="button"
                size="sm"
                variant={currentTab === tab.id ? "secondary" : "ghost"}
                disabled={tab.id === "mine" && !userId}
                onClick={() => applyTab(tab.id)}
                className="h-8 rounded-sm px-4"
              >
                {tab.label}
              </Button>
            ))}
          </div>
          <p className="text-muted-foreground text-xs">
            ຄິວທີ່ຈົບແລ້ວ (progress 100% / ຍົກເລີກ) ຢູ່ລຸ່ມ · ຄຳຂໍໃໝ່ຂຶ້ນກ່ອນ · ເລກນ້ອຍ =
            ສຳຄັນກວ່າ (1 ກ່ອນ 2) · ແທັບ &quot;ສຳຄັນກ່ອນ&quot; ສະແດງພຽງລາຍການທີ່ກຳນົດລຳດັບແລ້ວ
          </p>
        </div>

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <RequestFilter />

          {view === "calendar" ? (
            <div className="p-2 pb-4">
              <PerformanceRequestCalendar
                filters={filters}
                calendarMonth={calendarMonth}
                onCalendarMonthChange={(month) =>
                  updateSearch({ calendarMonth: month })
                }
              />
            </div>
          ) : (
            <RequestTable
              data={list.data?.data ?? []}
              isLoading={list.isLoading}
              offset={search.offset ?? 0}
              limit={search.limit ?? 20}
              totalCount={list.data?.meta?.total ?? 0}
              onPaginationChange={(offset, limit) =>
                nav({ search: { ...search, offset, limit } })
              }
              sortBy={sort[0]?.field}
              sortOrder={sort[0]?.dir}
              onSortingChange={(id, desc) =>
                nav({
                  search: {
                    ...search,
                    sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                  },
                })
              }
              onView={(request) =>
                nav({
                  to: "/app/performance/requests/$id",
                  params: { id: request.id },
                })
              }
              onEdit={(request) =>
                nav({
                  to: "/app/performance/requests/$id/edit",
                  params: { id: request.id },
                })
              }
              onDelete={(id) => {
                toast.promise(deleteRequest.run(id), {
                  loading: "ກຳລັງລຶບ...",
                  success: "ລຶບຄຳຂໍສຳເລັດ",
                  error: "ລຶບຄຳຂໍບໍ່ສຳເລັດ",
                });
              }}
              canConvert={canConvert}
              onConvert={(request) =>
                nav({
                  to: "/app/performance/requests/$id/convert",
                  params: { id: request.id },
                })
              }
              onOpenEvent={(request) => {
                if (!request.convertedEventId) return;
                nav({
                  to: "/app/performance/events/$id",
                  params: { id: request.convertedEventId },
                });
              }}
            />
          )}
        </div>
      </Main>
    </>
  );
}
